import { useEffect, useRef, useState } from 'react'

/**
 * Tegnefelt til montørens underskrift.
 *
 * Skrives med en finger på en telefon, så feltet er højt nok til at der er plads
 * til at skrive, og siden må ikke rulle imens.
 *
 * <b>En finger tegner med touch-hændelser, en mus eller pen med pointer-hændelser.</b>
 * Feltet brugte først kun pointer-hændelser med `setPointerCapture`, og på en iPhone
 * (appen på hjemmeskærmen) kom der slet ingen streg - mens det virkede på en computer.
 * Touch-hændelser er de stabile på både iPhone og Android. De lyttes der på direkte på
 * canvas med `passive: false`: React lytter passivt på berøringer, så et
 * `preventDefault` dér kan ikke forhindre siden i at rulle.
 *
 * Underskriften afleveres som PNG i en data-URL, som backenden gemmer sammen med
 * rapporten (docs/DECISIONS.md beslutning 026).
 */
export function SignaturePad({
  onChange,
  disabled = false
}: {
  /** Kaldes med en PNG-data-URL, eller null når feltet er tomt. */
  onChange: (dataUrl: string | null) => void
  disabled?: boolean
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const hasInk = useRef(false)
  const last = useRef<{ x: number; y: number } | null>(null)
  const [empty, setEmpty] = useState(true)

  // De native touch-lyttere sættes op én gang og skal altid se de nyeste værdier.
  const disabledRef = useRef(disabled)
  const onChangeRef = useRef(onChange)
  disabledRef.current = disabled
  onChangeRef.current = onChange

  /**
   * Giver canvas sin egen opløsning, så stregen ikke bliver grynet på mobil.
   * Returnerer false, hvis feltet endnu ikke har nogen størrelse.
   */
  function fit(): boolean {
    const canvas = canvasRef.current
    if (!canvas) return false

    const rect = canvas.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return false

    const ratio = window.devicePixelRatio || 1
    const width = Math.round(rect.width * ratio)
    const height = Math.round(rect.height * ratio)

    // En ændring af width/height rydder canvas, så det gøres kun ved reel ændring.
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width
      canvas.height = height
    }

    const context = canvas.getContext('2d')
    if (!context) return false

    // Sættes hver gang: en ny størrelse nulstiller både skalering og stregtype.
    context.setTransform(ratio, 0, 0, ratio, 0, 0)
    context.lineWidth = 2.5
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.strokeStyle = '#0F172A'
    return true
  }

  function pointAt(clientX: number, clientY: number) {
    const rect = canvasRef.current!.getBoundingClientRect()
    return { x: clientX - rect.left, y: clientY - rect.top }
  }

  function begin(clientX: number, clientY: number) {
    if (disabledRef.current || !canvasRef.current) return
    // Størrelsen tjekkes her og ikke kun ved visning: har feltet ikke haft en
    // størrelse endnu, da siden blev tegnet, ville der ellers aldrig komme en streg.
    if (!fit()) return

    last.current = pointAt(clientX, clientY)
    drawing.current = true
  }

  function extend(clientX: number, clientY: number) {
    if (!drawing.current || disabledRef.current || !last.current) return

    const context = canvasRef.current?.getContext('2d')
    if (!context) return

    const point = pointAt(clientX, clientY)
    context.beginPath()
    context.moveTo(last.current.x, last.current.y)
    context.lineTo(point.x, point.y)
    context.stroke()
    last.current = point

    if (!hasInk.current) {
      hasInk.current = true
      setEmpty(false)
    }
  }

  function finish() {
    if (!drawing.current) return

    drawing.current = false
    last.current = null

    if (hasInk.current) {
      onChangeRef.current(canvasRef.current?.toDataURL('image/png') ?? null)
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    fit()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => fit())
    observer?.observe(canvas)

    const onTouchStart = (event: TouchEvent) => {
      if (event.cancelable) event.preventDefault()
      const touch = event.changedTouches[0]
      if (touch) begin(touch.clientX, touch.clientY)
    }
    const onTouchMove = (event: TouchEvent) => {
      if (event.cancelable) event.preventDefault()
      const touch = event.changedTouches[0]
      if (touch) extend(touch.clientX, touch.clientY)
    }
    const onTouchEnd = (event: TouchEvent) => {
      if (event.cancelable) event.preventDefault()
      finish()
    }

    canvas.addEventListener('touchstart', onTouchStart, { passive: false })
    canvas.addEventListener('touchmove', onTouchMove, { passive: false })
    canvas.addEventListener('touchend', onTouchEnd, { passive: false })
    canvas.addEventListener('touchcancel', onTouchEnd, { passive: false })

    return () => {
      observer?.disconnect()
      canvas.removeEventListener('touchstart', onTouchStart)
      canvas.removeEventListener('touchmove', onTouchMove)
      canvas.removeEventListener('touchend', onTouchEnd)
      canvas.removeEventListener('touchcancel', onTouchEnd)
    }
    // Lytterne læser props gennem refs, så de skal kun sættes op én gang.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Mus og pen. En finger håndteres af touch-lytterne ovenfor, så den springes over
  // her - ellers ville den tegne to gange på telefoner, der sender begge dele.
  function onPointerDown(event: React.PointerEvent<HTMLCanvasElement>) {
    if (event.pointerType === 'touch' || disabled) return
    event.preventDefault()
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      // Uden capture ender stregen bare, når markøren forlader feltet.
    }
    begin(event.clientX, event.clientY)
  }

  function onPointerMove(event: React.PointerEvent<HTMLCanvasElement>) {
    if (event.pointerType === 'touch') return
    extend(event.clientX, event.clientY)
  }

  function onPointerEnd(event: React.PointerEvent<HTMLCanvasElement>) {
    if (event.pointerType === 'touch') return
    finish()
  }

  function clear() {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!canvas || !context) return

    context.save()
    context.setTransform(1, 0, 0, 1, 0, 0)
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.restore()

    hasInk.current = false
    setEmpty(true)
    onChange(null)
  }

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="signature-pad"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerLeave={onPointerEnd}
        onPointerCancel={onPointerEnd}
        // Ligger feltet i en <label>, sender browseren klikket efter en streg videre
        // til labelens første knap - "Ryd" - og underskriften forsvinder med det samme.
        onClick={(event) => event.preventDefault()}
      />

      <div className="row row--between" style={{ marginTop: 6 }}>
        <span className="tiny muted">
          {empty ? 'Skriv under med fingeren i feltet' : 'Underskrift registreret'}
        </span>
        <button className="btn btn--sm" type="button" disabled={empty || disabled} onClick={clear}>
          Ryd
        </button>
      </div>
    </div>
  )
}
